import * as React from 'react';
import { Card, CardContent, Typography, Box, styled, Divider } from '@mui/material';
import AndroidIcon from 'icons/Technologies/Android.icon';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from '@tanstack/react-router';

const StyledCard = styled(Card)(({theme}) => ({
  borderRadius: '12px',
  background: '#FFFFFF',
  border: '1px solid var(--Light-Gray)',
  padding: '32px 32px 24px 32px',
  boxShadow: '0px 2px 4px 0px #0205111F',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '24px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
  },
}));

const IconWrapper = styled(Box)(() => ({
  width: '64px',
  height: '64px',
}));

const DifficultyBadge = styled(Typography)(() => ({
  color: 'var(--Interviewer-Lighter-Black)',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
}));

const StyledDivider = styled(Divider)(() => ({
  height: '1px',
  width: '100%',
  margin: '0',
  borderColor: '#F0F1F3',
}));

// const StepIndicator = styled(Box)<{ isActive?: boolean }>(({ isActive = false }) => ({
//   width: '32px',
//   height: '2px',
//   backgroundColor: isActive ? 'var(--Interviewer-Green)' : 'var(--Light-Gray)',
//   borderRadius: '0 0 2px 2px',
// }));

const StepIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'color',
})<{ isActive?: boolean; color?: string }>(({ isActive = false, color }) => ({
  width: '32px',
  height: '2px',
  backgroundColor: isActive ? color : 'var(--Light-Gray)',
  borderRadius: '0 0 2px 2px',
}));

const StepContainer = styled(Box)(() => ({
  display: 'flex',
  gap: '4px',
}));

export type InterviewCardProps = {
  id: string;
  title: string;
  difficulty: string;
  description: string;
  activeStep?: number;
};

const getIconColor = (difficulty: string) => {
  switch (difficulty.toLowerCase()) {
    case 'entry':
      return '#31C07D';
    case 'advanced':
      return '#FF9800';
    case 'professional':
      return '#FF4842';
    default:
      return '#34A853';
  }
};

const getStepColor = (difficulty: string) => {
  switch (difficulty.toLowerCase()) {
    case 'entry':
      return '#31C07D';
    case 'advanced':
      return '#FF9800';
    case 'professional':
      return '#FF4842';
    default:
      return '#34A853';
  }
};

const getStepCount = (difficulty: string) => {
  switch (difficulty.toLowerCase()) {
    case 'entry':
      return 1;
    case 'advanced':
      return 2;
    case 'professional':
      return 3;
    default:
      return 1;
  }
};

function InterviewCard({
  id,
  title,
  difficulty,
  description,
}: InterviewCardProps) {

  const navigate = useNavigate();

  const startInterviewMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        'https://interviewer.runasp.net/api/submissionInterviews',
        {
          interviewId: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      navigate({ 
        to: '/questions/$id', 
        params: { id: data.id },
        state: { readTime: data.readTime, interviewId: id }
      });
    },
    onError: (error) => {
      console.log(error);
    }
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <StyledCard onClick={() => startInterviewMutation.mutate()}>
        <Box sx={{ display: 'flex'}}>
          <IconWrapper>
            <AndroidIcon color={getIconColor(difficulty)} />
          </IconWrapper>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <StepContainer>
              {[0, 1, 2].map((step) => (
                <StepIndicator
                  key={step}
                  isActive={step < getStepCount(difficulty)}
                  color={getStepColor(difficulty)}
                />
              ))}
            </StepContainer>
            <Typography variant="h2">{title}</Typography>
            <DifficultyBadge>Difficulty: {difficulty}</DifficultyBadge>
          </Box>
        </Box>
        <StyledDivider />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </StyledCard>
    </Box>
  );
}

export default InterviewCard; 